import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../admin/servicios/firebase';
import QRCode from 'react-qr-code';

export default function VerTicket() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const docRef = doc(db, 'tickets', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setTicket({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Boleto no encontrado.');
        }
      } catch (err) {
        console.error(err);
        setError('Error de conexión.');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [id]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif', color: '#64748b' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🔄</div>
      Cargando boleto...
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
      <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: 18 }}>{error}</p>
      <p style={{ color: '#94a3b8', fontSize: 13 }}>Verifica que el enlace sea correcto o contacta a taquilla.</p>
    </div>
  );

  const esRedondo = ticket.tipoViaje === 'redondo';
  const cancelado = ticket.estado === 'cancelado' || ticket.estado === 'cancelado_sin_reembolso';
  const usado = ticket.estado === 'usado';

  // Datos normalizados — soporta tanto boletos online como de taquilla
  const nombrePasajero = ticket.contacto?.nombre || ticket.nombrePasajero || 'Pasajero';
  const fechaIda       = esRedondo ? (ticket.fechaIda  || ticket.fechaViaje) : ticket.fechaViaje;
  const horaIda        = esRedondo ? (ticket.horaIda   || ticket.horaViaje)  : ticket.horaViaje;
  const origenIda      = esRedondo ? ticket.rutaIda?.origen  : ticket.ruta?.origen;
  const destinoIda     = esRedondo ? ticket.rutaIda?.destino : ticket.ruta?.destino;
  const asientosIda    = (esRedondo ? ticket.asientosIda : ticket.asientos) || [];
  const precioTotal    = ticket.precioTotal || ticket.precio || null;
  const vendidoEn      = ticket.metodoPago === 'efectivo_taquilla' ? 'Taquilla' : 'En línea';

  // Color del banner de estado
  const bannerColor = cancelado ? '#dc2626' : usado ? '#d97706' : '#16a34a';
  const bannerBg    = cancelado ? '#fef2f2' : usado ? '#fffbeb' : '#f0fdf4';
  const bannerText  = cancelado ? '🚫 BOLETO CANCELADO'
                    : usado     ? '⚠️ BOLETO YA UTILIZADO'
                    :             '✅ BOLETO DE ÚNICO USO — VÁLIDO';

  const s = {
    page:      { fontFamily: "'Segoe UI', sans-serif", maxWidth: 420, margin: '0 auto', background: '#f8fafc', minHeight: '100vh', paddingBottom: 40 },
    card:      { background: 'white', margin: '0 0 12px', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    banner:    { background: bannerBg, border: `1px solid ${bannerColor}30`, borderRadius: 12, margin: '16px 16px 0', padding: '12px 16px', textAlign: 'center', color: bannerColor, fontWeight: 900, fontSize: 14 },
    header:    { background: '#0f172a', padding: '20px 20px 16px', textAlign: 'center' },
    empresa:   { color: 'white', fontSize: 22, fontWeight: 900, textTransform: 'uppercase', margin: 0, letterSpacing: 1 },
    subtitulo: { color: '#94a3b8', fontSize: 12, margin: '4px 0 0' },
    badge:     { display: 'inline-block', background: esRedondo ? '#7c3aed' : '#059669', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20, marginTop: 10 },
    body:      { padding: '16px 20px' },
    label:     { fontSize: 9, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700, letterSpacing: 0.5, marginBottom: 2 },
    valor:     { fontSize: 18, fontWeight: 900, color: '#0f172a', margin: '0 0 12px' },
    valorSm:   { fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' },
    row:       { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 },
    col:       { flex: 1 },
    ruta:      { background: '#f8fafc', borderRadius: 10, padding: '12px 14px', marginBottom: 12, border: '1px solid #e2e8f0' },
    rutaLabel: { fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 },
    rutaTexto: { fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
    asiento:   { background: '#0f172a', color: 'white', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    divider:   { border: 'none', borderTop: '1px dashed #e2e8f0', margin: '4px 0 12px' },
    qrWrap:    { textAlign: 'center', padding: '20px 20px 16px', background: 'white' },
    qrLabel:   { fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
    qrId:      { fontFamily: 'monospace', fontSize: 13, color: '#475569', marginTop: 10, fontWeight: 700 },
    // Aviso de conservar boleto
    aviso:     { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, margin: '0 16px 12px', padding: '12px 16px' },
    avisoTit:  { color: '#1d4ed8', fontWeight: 800, fontSize: 13, marginBottom: 4 },
    avisoTxt:  { color: '#3730a3', fontSize: 11, lineHeight: 1.5, margin: 0 },
    // Términos
    terminos:  { background: 'white', borderRadius: 16, margin: '0 0 12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
    termHead:  { background: '#1d4ed8', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 },
    termTit:   { color: 'white', fontWeight: 800, fontSize: 13, margin: 0 },
    termBody:  { padding: '14px 16px' },
    termSec:   { marginBottom: 12 },
    termLabel: { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: '#0f172a', marginBottom: 4 },
    termTxt:   { fontSize: 11, color: '#475569', lineHeight: 1.6, margin: 0 },
    termRojo:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', textAlign: 'center', color: '#dc2626', fontWeight: 700, fontSize: 11, marginTop: 8 },
    termDir:   { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
    termDirTit:{ color: '#1d4ed8', fontWeight: 800, fontSize: 11, marginBottom: 4 },
    termDirTxt:{ color: '#334155', fontSize: 11, margin: 0, lineHeight: 1.5 },
    footer:    { textAlign: 'center', padding: '0 16px 8px', color: '#94a3b8', fontSize: 11 },
  };

  return (
    <div style={s.page}>

      {/* ── BANNER ESTADO ── */}
      <div style={s.banner}>{bannerText}</div>

      {/* ── HEADER EMPRESA ── */}
      <div style={{ ...s.card, marginTop: 12 }}>
        <div style={s.header}>
          <p style={s.empresa}>Ángeles del Puerto</p>
          <p style={s.subtitulo}>Transportes Ángeles del Puerto · Boleto Digital</p>
          <span style={s.badge}>{esRedondo ? '⮂ Boleto Redondo' : '→ Boleto Sencillo'}</span>
        </div>

        <div style={s.body}>

          {/* Pasajero */}
          <div style={s.label}>Pasajero</div>
          <p style={s.valor}>{nombrePasajero}</p>

          {/* Ruta IDA */}
          <div style={s.ruta}>
            <div style={s.rutaLabel}>{esRedondo ? '✈ Ida' : '✈ Ruta'}</div>
            <div style={s.rutaTexto}>
              <span>{origenIda}</span>
              <span style={{ color: '#94a3b8', fontSize: 18 }}>→</span>
              <span>{destinoIda}</span>
            </div>
          </div>

          {/* Fecha / Hora */}
          <div style={s.row}>
            <div style={s.col}>
              <div style={s.label}>Fecha</div>
              <p style={s.valorSm}>{fechaIda || '—'}</p>
            </div>
            <div style={s.col}>
              <div style={s.label}>Hora</div>
              <p style={s.valorSm}>{horaIda || '—'}</p>
            </div>
          </div>

          {/* Asiento + precio */}
          <div style={s.asiento}>
            <div>
              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Asiento(s) reservado(s)</div>
              <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2 }}>
                {asientosIda.length > 0 ? asientosIda.map(a => a.replace('a', '')).join(', ') : '—'}
              </div>
            </div>
            {precioTotal && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Pagado</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#4ade80' }}>${precioTotal}</div>
              </div>
            )}
          </div>

          {/* Sección REGRESO si aplica */}
          {esRedondo && (
            <>
              <hr style={s.divider} />
              <div style={{ ...s.ruta, background: '#faf5ff', border: '1px solid #e9d5ff' }}>
                <div style={{ ...s.rutaLabel, color: '#7c3aed' }}>⮂ Regreso</div>
                <div style={s.rutaTexto}>
                  <span>{ticket.rutaRegreso?.origen || destinoIda}</span>
                  <span style={{ color: '#a78bfa', fontSize: 18 }}>→</span>
                  <span>{ticket.rutaRegreso?.destino || origenIda}</span>
                </div>
              </div>
              <div style={s.row}>
                <div style={s.col}>
                  <div style={s.label}>Fecha regreso</div>
                  <p style={s.valorSm}>{ticket.fechaRegreso || '—'}</p>
                </div>
                <div style={s.col}>
                  <div style={s.label}>Hora regreso</div>
                  <p style={s.valorSm}>{ticket.horaRegreso || '—'}</p>
                </div>
              </div>
              {ticket.asientosRegreso?.length > 0 && (
                <div style={{ ...s.asiento, background: '#4c1d95' }}>
                  <div>
                    <div style={{ fontSize: 9, color: '#c4b5fd', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Asiento(s) regreso</div>
                    <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2 }}>
                      {ticket.asientosRegreso.map(a => a.replace('a', '')).join(', ')}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Vendido en */}
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', marginTop: -4, marginBottom: 4 }}>
            Vendido en: <strong style={{ color: '#64748b' }}>{vendidoEn}</strong>
          </div>
        </div>

        {/* QR */}
        <div style={s.qrWrap}>
          <div style={s.qrLabel}>📷 Escanea para validar abordaje</div>
          <div style={{ display: 'inline-block', padding: 12, background: 'white', border: '2px solid #e2e8f0', borderRadius: 12 }}>
            <QRCode value={ticket.id} size={180} />
          </div>
          <p style={s.qrId}>ID: {ticket.id}</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
            Muestra este código al conductor al abordar.<br />No requiere impresión.
          </p>
        </div>
      </div>

      {/* ── AVISO: CONSERVA TU BOLETO ── */}
      <div style={s.aviso}>
        <div style={s.avisoTit}>🛡️ Conserve este documento</div>
        <p style={s.avisoTxt}>
          Este boleto constituye el comprobante oficial de su reservación y pago de viaje.
          Le recomendamos conservarlo durante todo el trayecto, ya que acredita
          su lugar asignado, la ruta contratada y el monto liquidado.
          En caso de cualquier eventualidad, deberá presentarlo ante el personal de la empresa.
        </p>
      </div>

      {/* ── TÉRMINOS Y CONDICIONES ── */}
      <div style={{ ...s.terminos, margin: '0 0 12px' }}>
        <div style={s.termHead}>
          <span style={{ fontSize: 16 }}>📋</span>
          <p style={s.termTit}>Términos y Condiciones</p>
        </div>
        <div style={s.termBody}>

          {/* Punto de abordaje */}
          <div style={s.termDir}>
            <div style={s.termDirTit}>📍 Punto de Abordaje</div>
            <p style={s.termDirTxt}>
              <strong>Terminal Centro (PLAZA 522)</strong><br />
              Galeana 522, Zona Lunes Feb 09, Centro,<br />
              68000 Oaxaca de Juárez, Oax.
            </p>
          </div>

          {/* Abordaje y validez */}
          <div style={s.termSec}>
            <div style={s.termLabel}>🕒 Abordaje y Validez</div>
            <p style={s.termTxt}>
              El boleto es personal e intransferible. Válido solo para la fecha y hora marcada.
              Presentarse <strong>15 minutos antes</strong> de la salida.
              Obligatorio presentar identificación oficial vigente al abordar.
              Viaje con tranquilidad. Su boleto lo protege con seguro de viajero contra accidentes desde el momento en que aborda hasta llegar a su destino.

            </p>
          </div>

          {/* Equipaje */}
          <div style={s.termSec}>
            <div style={s.termLabel}>🧳 Política de Equipaje</div>
            <p style={s.termTxt}>
              Derecho a transportar equipaje de uso personal. El exceso o mercancía generará costo extra.
              La empresa no se hace responsable por objetos de valor no reportados.
            </p>
          </div>

          {/* Cancelaciones */}
          <div style={s.termSec}>
            <div style={s.termLabel}>🔄 Cambios y Cancelaciones</div>
            <p style={{ ...s.termTxt, color: '#dc2626', fontWeight: 700, marginBottom: 4 }}>
              NO HAY REEMBOLSOS EN EFECTIVO PARA COMPRAS EN TAQUILLA.
            </p>
            <p style={s.termTxt}>
              Solo se permiten cambios de fecha con al menos 2 horas y media de anticipación,
              sujeto a disponibilidad y cargo del 10%.
            </p>
          </div>

          {/* Prohibiciones */}
          <div style={s.termRojo}>
            🚫 PROHIBIDO ARMAS Y ALCOHOL<br />
            <span style={{ fontWeight: 400 }}>
              Nos reservamos el derecho de admisión a personas bajo efectos del alcohol y drogas.
            </span>
          </div>

          {/* Contacto */}
          <div style={{ textAlign: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Sugerencias y quejas:</p>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#475569', margin: '2px 0 0' }}>954 167 7156</p>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>transportesangelesdelpuerto.mx</p>
          </div>
        </div>
      </div>

      <div style={s.footer}>
        Boleto de único uso · Personal e intransferible · Sin reembolso
      </div>

    </div>
  );
}